import { ApiError } from "@jobenvoy/core";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { SearchPosting } from "@/lib/api/catalogue";
import type { Application } from "@/lib/api/envoy";
import { keepLastSearch } from "@/lib/envoySeed";
import {
    oncePerPosting,
    usePosting,
    usePostings,
    useSearchCounts,
    useSearchPage,
} from "@/lib/hooks/catalogue";
import { useApplicationsFor, useTrackedApplications, useTrackedTotals } from "@/lib/hooks/envoy";
import { useEnvoyArea } from "@/lib/hooks/envoyArea";
import { useMostKeywords, usePostingFits } from "@/lib/hooks/resume";
import { jobLocationQuery, readJobLocation } from "@/lib/jobLocation";
import {
    askedIds,
    askedPostings,
    bestMatchNear,
    boardPage,
    catalogueCursor,
    countsFromEnvoy,
    isTracked,
    jobsParams,
    jobsQuery,
    jobsSource,
    localStore,
    pagerKey,
    readJobsView,
    sortOf,
    trackedParams,
    trackedPostings,
    trackedShown,
    type CompanyRef,
    type KeptPage,
    type JobsView,
} from "@/lib/jobs";
import { visitorLanguages } from "@/lib/languages";
import { useCursorPager } from "@/lib/useCursorPager";

/**
 * The Jobs search: the view read from the address and the way to change
 * it, the page of results and the counts, the posting chosen when it is
 * not on the page, and for a member where each application stands and
 * the fit of their resume. Best match is the resume service's ranking
 * for a member with a resume, and the catalogue's order for anyone else.
 * With a company it is that company's jobs, held to nothing the person
 * did not ask for: the browser's languages are not assumed. A member's In
 * queue and Applied tabs are their applications in envoy, a page at a
 * time, at the company with one, each posting read from the catalogue;
 * `board` says the page's count, total and Next from whichever list the
 * tab reads.
 */
/** An envoy tab's page before it is read, one array so the memos over it hold. */
const NO_APPLICATIONS: Application[] = [];

export function useJobsSearch(member: boolean, company?: CompanyRef) {
    const [params, setParams] = useSearchParams();
    const [visitor] = useState(visitorLanguages);
    const browser = company ? [] : visitor;
    const view = readJobsView(params, browser, company);
    const place = readJobLocation(params);
    // Once the catalogue has said it cannot tell where the person is, a
    // distance is not asked for again: the search is of every distance.
    const [nowhere, setNowhere] = useState(false);
    // The search the ranking could not be had for: no resume, or a
    // catalogue too busy. It is shown in the catalogue's order instead.
    const [unrankedFor, setUnrankedFor] = useState<string | null>(null);
    const bestMatch = sortOf(view) === "RELEVANCE";
    const area = useEnvoyArea(member && bestMatch);
    // Best match waits for the city, so it does not search from the edge first.
    const ready = !(member && bestMatch && area.isPending);
    const asked = { ...jobsQuery(view), ...bestMatchNear(view, place, area.data ?? null) };
    // The search writes the words to the address once typing rests, so
    // the search asks once for the words, not once a key.
    const query = {
        ...asked,
        within_miles: nowhere ? undefined : asked.within_miles,
        ...jobLocationQuery(place),
    };
    const searched = JSON.stringify(query);
    const unranked = unrankedFor === searched;
    const source = jobsSource(member, view.status);
    // A cursor is only good for the list that gave it, so a search that
    // falls back to the catalogue's order starts from its first page, and
    // each envoy tab is a list of its own, each company's apart.
    const list = pagerKey(source, view.status, `${searched}:${String(unranked)}`, company);
    const pager = useCursorPager(list);
    const cursor = catalogueCursor(source, pager.cursor);
    // The resume service has no edge in front of its search: a distance
    // needs the point named.
    const rankable =
        member &&
        bestMatch &&
        !unranked &&
        (query.within_miles === undefined || query.near_latitude !== undefined);
    const ranked = useMostKeywords(query, cursor, rankable && ready);
    const listed = useSearchPage(query, cursor, ready && !rankable);
    const page = rankable ? ranked : listed;
    if (rankable && (ranked.isError || (ranked.data === null && !ranked.isPlaceholderData)))
        setUnrankedFor(searched);
    const counts = useSearchCounts(ready ? { ...query, sort: undefined, limit: undefined } : null);
    const unplaced = isNowhere(page.error) || isNowhere(counts.error);
    // Learned during the render that saw the refusal, so the next asks
    // without the distance at once.
    if (unplaced && !nowhere) setNowhere(true);

    const results = rankable ? ranked.data?.results : undefined;
    const postings: SearchPosting[] = useMemo(
        () =>
            oncePerPosting(
                results ? results.map((result) => result.posting) : (listed.data?.data ?? []),
            ),
        [results, listed.data],
    );
    const rankedFits = useMemo(
        () => Object.fromEntries((results ?? []).map(({ fit }) => [fit.posting_id, fit])),
        [results],
    );

    // An envoy tab: the person's applications, at the company on its page,
    // each posting from the catalogue.
    const tab = isTracked(view.status) ? view.status : null;
    // Only what belongs on the tab, whatever an older envoy sends.
    const tracked = useTrackedApplications(
        source === "envoy" && tab !== null
            ? trackedParams(tab, pager.cursor, undefined, company)
            : null,
        tab,
        company,
    );
    const onTab = tracked.data?.applications ?? NO_APPLICATIONS;
    const trackedIds = useMemo(() => onTab.map((application) => application.posting_id), [onTab]);
    const trackedReads = usePostings(trackedIds);
    const loaded = useMemo(
        () => trackedPostings(onTab, trackedReads.postings),
        [onTab, trackedReads.postings],
    );
    // The tab's last page, to stand in while the next page's postings are read.
    const [kept, setKept] = useState<KeptPage | null>(null);
    const shown = trackedShown(list, loaded, trackedReads.pending, kept);
    if (source === "envoy" && shown.keep !== null && shown.keep !== kept) setKept(shown.keep);
    const shownPostings = source === "envoy" ? shown.postings : postings;
    const envoyCounts = countsFromEnvoy(member);
    const totals = useTrackedTotals(envoyCounts, company);
    const board = boardPage(
        source,
        shownPostings.length,
        {
            read: {
                pending: page.isPending,
                error: page.isError ? page.error : null,
                stale: page.isPlaceholderData,
            },
            total: counts.data?.total,
            nextCursor: page.data?.next_cursor,
        },
        {
            read: {
                pending: tracked.isPending || shown.pending,
                error: tracked.isError ? tracked.error : null,
                stale: tracked.isPlaceholderData || shown.stale,
            },
            total: tracked.data?.total,
            nextCursor: tracked.data?.next_cursor,
        },
    );

    const onPage = shownPostings.find((posting) => posting.posting_id === view.posting);
    const elsewhere = usePosting(
        view.posting && !onPage && !board.pending && !board.error ? view.posting : null,
    );
    // A visitor has no applications and no resume to score: nothing is asked.
    // The catalogue's page is asked on every tab, for Untracked's count.
    const ids = member
        ? askedIds(askedPostings(source, shownPostings, postings), onPage ? null : elsewhere.data)
        : [];
    const applications = useApplicationsFor(ids);
    // A page of the ranking came with its fits: only one open from
    // elsewhere is asked for.
    const fits = usePostingFits(ids.filter((id) => !(id in rankedFits)));
    const update = (patch: Partial<JobsView>) =>
        setParams(jobsParams({ ...view, ...patch }, browser, place), { replace: true });

    // The search the envoy would start from, for Get new matches and Turn on.
    const search = params.toString();
    const lastSearch = company ? null : search;
    useEffect(() => {
        if (lastSearch !== null) keepLastSearch(localStore(), lastSearch);
    }, [lastSearch]);

    return {
        view,
        place,
        /** The one company the page is of, when it is one company's. */
        company,
        /** Where the tab's list comes from. */
        source,
        /** A distance is set, and the catalogue cannot tell where the person is. */
        nowhere: view.within !== null && (nowhere || unplaced),
        update,
        pager,
        /** The catalogue's page of the search. */
        page,
        counts,
        /** The board's page: its count, total and Next, from the list the tab reads. */
        board,
        /** The board's postings: the catalogue's page, or an envoy tab's. */
        postings: shownPostings,
        /** The catalogue's page, whichever tab is open. */
        cataloguePostings: postings,
        /**
         * How many are in the person's queue and how many went in, over
         * every page. Null where the tabs count the page instead.
         */
        totals: envoyCounts ? totals : null,
        onPage,
        elsewhere: elsewhere.data,
        applications,
        fits: results ? { ...fits.data, ...rankedFits } : fits.data,
    };
}

/** The catalogue's refusal of a distance when the edge did not say where the person is. */
export function isNowhere(error: unknown): boolean {
    return (
        error instanceof ApiError &&
        error.status === 400 &&
        error.body.includes("somewhere to measure from")
    );
}
